import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolSheetMaterial } from "../../../../../http/patrol/types/patrolSheetMaterials";
import { usePatrolSheetMaterialsApi } from "../../../../../http/patrol/usePatrolSheetMaterials";
import { PaginationQuery } from "../../../../../types";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import SheetMaterialForm from "./SheetMaterialForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SheetMaterials: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "sheetMaterials" });
  const aborter = useRef(new AbortController());
  const sheetMaterialsApi = usePatrolSheetMaterialsApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await sheetMaterialsApi.updateAdminPatrolSheetMaterialVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (sheetMaterialId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await sheetMaterialsApi.updateAdminPatrolSheetMaterialOrder(sheetMaterialId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<AdminPatrolSheetMaterial>();
  const [itemToDelete, setItemToDelete] = useState<AdminPatrolSheetMaterial>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminPatrolSheetMaterial>("patrol.sheet_material");
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "sheetMaterials");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = sheetMaterialsApi.useAdminPatrolSheetMaterials(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.sheetMaterials.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.sheetMaterials.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminPatrolSheetMaterial>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.materials.singular}</th>
            <th align="left">{i18n.translation.common.minValue}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/sheetMaterials/${item.id}`}>
                {item.material}
              </Link>
            </td>
            <td>{item.minValue} mm</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.sheetMaterials.createNew}>
          <SheetMaterialForm onClose={() => setShowNewItemDialog(false)} />
        </ModalDialog>
      )}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.sheetMaterials.edit}>
          <SheetMaterialForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.sheetMaterials.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                sheetMaterialsApi
                  .deleteAdminPatrolSheetMaterial(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.sheetMaterials.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default SheetMaterials;
